/**
 * SimTopUpDialog (DE-266) — set / raise / clear the Onomondo hard data cap.
 *
 * One dialog, two shapes:
 *  - single: one device, seeded with its current cap; quick "+N" chips raise
 *    from the current value, and the expectedIccid the server demands is the
 *    one this dialog was opened with (a SIM swap between render and confirm
 *    409s instead of capping the wrong card).
 *  - bulk: N devices get the SAME absolute cap (relative raises make no sense
 *    across mixed current caps); per-device outcomes render in place so a
 *    partial failure is legible instead of a single toast.
 *
 * Semantics (E2E-verified 2026-08-31, docs/de266-onomondo-notes.md): Onomondo
 * has no prepaid top-up — the cap is a hard data_limit and this dialog
 * rewrites it. Rewriting the limit RESETS the period's used counter to zero,
 * so any cap change grants a fresh full allowance for the rest of the period
 * and unblocks a capped SIM immediately. The copy below says so, because
 * "raise by 200 MB" actually grants the whole new total.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useSimBulkLimit,
  useSimTopUp,
  type SimBulkLimitResponse,
  type SimQuotaSummary,
} from '../api/hooks';
import { fmtBytes } from './format';
import { useToast } from './Toast';

const MB = 1_000_000;
const GB = 1_000_000_000;
const MIN_LIMIT_BYTES = 500_000; // Onomondo's documented minimum

type Target =
  | { mode: 'single'; thing: string; sim: SimQuotaSummary }
  | { mode: 'bulk'; things: string[] };

export function SimTopUpDialog({
  target,
  onClose,
}: {
  target: Target;
  onClose: () => void;
}) {
  const single = target.mode === 'single' ? target : null;
  const currentTotal = single?.sim.quota?.totalBytes ?? null;

  // The input works in whole MB — operators think in MB/GB, not bytes.
  const [mb, setMb] = useState<string>(
    currentTotal !== null ? String(Math.round(currentTotal / MB)) : '1000',
  );
  const [removeCap, setRemoveCap] = useState(false);
  const [reason, setReason] = useState('');
  const toast = useToast();
  const topUp = useSimTopUp();
  const bulk = useSimBulkLimit();
  const [bulkResults, setBulkResults] = useState<SimBulkLimitResponse['results'] | null>(null);

  const busy = topUp.isPending || bulk.isPending;
  const totalBytes = removeCap ? null : Math.round(Number(mb) * MB);
  const valid =
    reason.trim().length > 0 &&
    (removeCap || (Number.isFinite(totalBytes!) && totalBytes! >= MIN_LIMIT_BYTES));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const bump = (deltaBytes: number) => {
    setRemoveCap(false);
    const base = Number(mb) * MB || currentTotal || 0;
    setMb(String(Math.round((base + deltaBytes) / MB)));
  };

  const summary = useMemo(() => {
    const capText = removeCap ? 'remove the data cap' : `set the data cap to ${fmtBytes(totalBytes ?? 0)}`;
    if (single) {
      const from =
        currentTotal !== null ? ` (currently ${fmtBytes(currentTotal)})` : ' (currently uncapped)';
      return (
        <>
          This will {capText} for <span className="font-mono">{single.thing}</span>
          {from}, SIM <span className="font-mono">{single.sim.simId}</span>
          {single.sim.quota?.resetsAt && (
            <> — the cap period resets {new Date(single.sim.quota.resetsAt).toLocaleDateString()}</>
          )}
          .
        </>
      );
    }
    return (
      <>
        This will {capText} on <strong>{target.mode === 'bulk' ? target.things.length : 0} devices</strong>.
        Devices without an Onomondo SIM are skipped and reported below.
      </>
    );
  }, [removeCap, totalBytes, single, currentTotal, target]);

  const submit = async () => {
    if (!valid || busy) return;
    if (single) {
      try {
        const r = await topUp.mutateAsync({
          thing: single.thing,
          totalBytes,
          expectedIccid: single.sim.iccid,
          reason: reason.trim(),
        });
        toast.success(
          r.sim?.quota
            ? `SIM cap for ${single.thing} is now ${fmtBytes(r.sim.quota.totalBytes)}`
            : `SIM cap removed for ${single.thing}`,
        );
        onClose();
      } catch (err) {
        toast.error((err as Error).message || 'SIM limit update failed');
      }
      return;
    }
    if (target.mode !== 'bulk') return;
    try {
      const r = await bulk.mutateAsync({
        things: target.things,
        totalBytes,
        reason: reason.trim(),
      });
      setBulkResults(r.results);
      const okCount = Object.values(r.results).filter((x) => x.ok).length;
      const failCount = Object.values(r.results).length - okCount;
      if (failCount === 0) {
        toast.success(`SIM cap updated on all ${okCount} devices`);
        onClose();
      } else {
        toast.error(`${okCount} updated, ${failCount} skipped/failed — see details`);
      }
    } catch (err) {
      toast.error((err as Error).message || 'bulk SIM limit update failed');
    }
  };

  const blockedNow = single?.sim.quota?.blocked ?? false;

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal-top"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sim-topup-title"
    >
      <div className="card w-full max-w-md space-y-4 shadow-xl">
        <div>
          <h2 id="sim-topup-title" className="font-semibold">
            {single ? 'SIM data cap' : `SIM data cap — ${target.mode === 'bulk' ? target.things.length : 0} devices`}
          </h2>
          <p className="text-xs text-muted mt-1">
            Onomondo bills by usage; the cap is a hard limit — a SIM that hits it passes no
            data until the period resets. “Adding quota” means raising this cap.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {single ? (
              <>
                {[
                  { label: '+100 MB', d: 100 * MB },
                  { label: '+500 MB', d: 500 * MB },
                  { label: '+1 GB', d: GB },
                ].map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    disabled={busy}
                    onClick={() => bump(c.d)}
                    className="px-2 py-1 text-xs rounded-md border border-border hover:bg-soft"
                  >
                    {c.label}
                  </button>
                ))}
              </>
            ) : (
              <>
                {[
                  { label: '500 MB', v: 500 },
                  { label: '1 GB', v: 1000 },
                  { label: '3 GB', v: 3000 },
                  { label: '5 GB', v: 5000 },
                ].map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setRemoveCap(false);
                      setMb(String(c.v));
                    }}
                    className="px-2 py-1 text-xs rounded-md border border-border hover:bg-soft"
                  >
                    {c.label}
                  </button>
                ))}
              </>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted w-24 shrink-0">Cap</span>
            <input
              type="number"
              min={1}
              value={mb}
              disabled={removeCap || busy}
              onChange={(e) => setMb(e.target.value)}
              className="w-28 border border-border rounded-md px-2 py-1 text-sm tabular-nums disabled:opacity-50"
            />
            <span className="text-muted text-xs">MB</span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={removeCap}
              disabled={busy}
              onChange={(e) => setRemoveCap(e.target.checked)}
            />
            <span>Remove cap entirely (uncapped — usage-based billing only)</span>
          </label>

          <label className="block text-sm">
            <span className="text-muted">Reason (required — recorded in the audit trail)</span>
            <input
              type="text"
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. OTA to 2026.09 needs ~800 MB"
              className="mt-1 w-full border border-border rounded-md px-2 py-1 text-sm"
            />
          </label>
        </div>

        <p className="text-xs text-muted">{summary}</p>
        {!removeCap && (
          <p className="text-xs text-muted">
            Note: changing the cap resets the period's used counter — the device gets the
            full new amount for the rest of the period{blockedNow && ', and data resumes immediately'}.
          </p>
        )}

        {bulkResults && (
          <div className="max-h-40 overflow-y-auto border border-border rounded-md">
            <table className="w-full text-xs">
              <tbody>
                {Object.entries(bulkResults).map(([thing, r]) => (
                  <tr key={thing} className="border-b border-border last:border-0">
                    <td className="px-2 py-1 font-mono">{thing}</td>
                    <td className={`px-2 py-1 ${r.ok ? 'text-accent' : 'text-warn'}`}>
                      {r.ok
                        ? r.sim?.quota
                          ? `ok — ${fmtBytes(r.sim.quota.totalBytes)}`
                          : 'ok — uncapped'
                        : r.error ?? 'failed'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted hover:text-fg rounded-md"
          >
            {bulkResults ? 'Close' : 'Cancel'}
          </button>
          {!bulkResults && (
            <button
              type="button"
              disabled={!valid || busy}
              onClick={() => void submit()}
              className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50"
            >
              {busy ? 'Applying…' : removeCap ? 'Remove cap' : 'Set cap'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
